import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { money } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Landmark, Wallet } from "lucide-react";

export default function WithdrawDialog({ open, onClose, max, onDone, endpoint = "/wallet/withdraw" }) {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [accountName, setAccountName] = useState("");
  const [swift, setSwift] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(endpoint, { amount: parseFloat(amount), method, destination, account_name: accountName, swift });
      toast.success(t("saved"));
      await refresh();
      onClose();
      onDone?.();
      setAmount(""); setDestination(""); setAccountName(""); setSwift("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="withdraw-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("withdraw_funds")}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{t("available_balance")}: <span className="text-primary font-semibold">{money(max)}</span></p>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">{t("amount")}</Label>
            <Input data-testid="withdraw-amount" required type="number" min="1" max={max} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 bg-secondary/50" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">{t("method")}</Label>
            <div className="grid grid-cols-2 gap-2">
              {[{ id: "paypal", icon: Wallet }, { id: "bank", icon: Landmark }].map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.id} type="button" data-testid={`withdraw-method-${m.id}`} onClick={() => setMethod(m.id)}
                    className={`flex items-center gap-2 justify-center h-11 rounded-xl border text-sm font-medium ${method === m.id ? "border-primary bg-primary/15" : "border-border bg-secondary/40 text-muted-foreground"}`}>
                    <Icon className="w-4 h-4" /> {t(m.id)}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">{method === "paypal" ? t("paypal_email") : t("iban")}</Label>
            <Input data-testid="withdraw-destination" required value={destination} onChange={(e) => setDestination(e.target.value)} className="mt-1 bg-secondary/50" placeholder={method === "paypal" ? "name@paypal.com" : "ES00 0000 0000 0000"} />
          </div>
          {method === "bank" && (
            <>
              <div>
                <Label className="text-xs">{t("account_name")}</Label>
                <Input data-testid="withdraw-account-name" required value={accountName} onChange={(e) => setAccountName(e.target.value)} className="mt-1 bg-secondary/50" placeholder="Nombre Apellidos" />
              </div>
              <div>
                <Label className="text-xs">{t("swift")}</Label>
                <Input data-testid="withdraw-swift" required value={swift} onChange={(e) => setSwift(e.target.value)} className="mt-1 bg-secondary/50" placeholder="BBVAESMMXXX" />
              </div>
            </>
          )}
          <Button data-testid="withdraw-submit-btn" type="submit" disabled={busy || max <= 0} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("request_withdraw")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
